/**
 * Central SEO Configuration Map
 * All route SEO metadata in one place for consistent application
 */

import { getRouteManifestEntry } from '@/config/routeManifest';

export interface RouteSchema {
  type: 'WebSite' | 'Organization' | 'AboutPage' | 'FAQPage' | 'CollectionPage' | 
        'Product' | 'SoftwareApplication' | 'WebPage' | 'Service' | 'ContactPage' | 
        'OfferCatalog';
  additionalTypes?: string[];
}

export interface RouteSEOConfig {
  title: string;
  description: string;
  ogType: 'website' | 'product' | 'article';
  schema: RouteSchema;
  noIndex?: boolean;
}

const BASE_URL = 'https://www.dialgia.com';
// Default OG image - using Supabase storage for consistent delivery
// To update: replace with new image URL or local /og-image.jpg when available
const DEFAULT_OG_IMAGE = 'https://evsdwuokuejkjqfyeafk.supabase.co/storage/v1/object/public/logos/dialgia-icon.png';

// Default title format function
export const formatTitle = (pageTitle: string): string => {
  if (pageTitle.includes('|')) return pageTitle;
  return `${pageTitle} | Dialgia Group`;
};

// Central SEO config map keyed by route path
export const SEO_CONFIG: Record<string, RouteSEOConfig> = {
  // Home
  '/': {
    title: 'Dialgia Group | Enterprise Websites, SaaS & AI Agents',
    description: 'Dialgia Group builds enterprise websites, SaaS platforms, and AI agents with secure delivery, premium UX, and scalable architecture for modern businesses.',
    ogType: 'website',
    schema: { type: 'WebSite', additionalTypes: ['Organization'] }
  },

  // About
  '/about': {
    title: 'About Dialgia Group | Built for Enterprise Delivery',
    description: 'Learn how Dialgia Group delivers enterprise-grade web and SaaS products with disciplined execution, secure engineering, and long-term partnership.',
    ogType: 'website',
    schema: { type: 'AboutPage', additionalTypes: ['Organization'] }
  },

  // FAQ
  '/faq': {
    title: 'Dialgia Group FAQ | Answers on Delivery, Pricing & Support',
    description: 'Find answers on Dialgia\'s delivery model, pricing, support, security, and how we build enterprise websites, SaaS products, and AI agents.',
    ogType: 'website',
    schema: { type: 'FAQPage' }
  },

  // Products
  '/products': {
    title: 'Dialgia Products | Enterprise Platforms Built to Scale',
    description: 'Explore Dialgia\'s enterprise product suite, from digital newsroom and QA systems to WFM, IT service desk, payroll, HR, and AI-enabled platforms.',
    ogType: 'website',
    schema: { type: 'CollectionPage' }
  },

  // Website & Web App Suite
  '/products/website-webapp-suite': {
    title: 'Website & Web App Suite | Dialgia Group',
    description: 'A complete website and web app build service, designed for speed, security, SEO, and conversion, delivered with enterprise governance and support.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  // AI Agents
  '/ai-agents': {
    title: 'AI Agents by Dialgia | Voice & Chat Assistants for Business',
    description: 'Deploy AI agents for customer support, operations, and internal teams with secure workflows, knowledge grounding, and human handoff when needed.',
    ogType: 'website',
    schema: { type: 'WebPage', additionalTypes: ['Service'] }
  },

  '/welcome': {
    title: 'Welcome to Dialgia | Enterprise AI & Digital Delivery',
    description: 'Discover how Dialgia combines enterprise software, AI agents, and expert delivery to simplify operations, improve customer experience, and reduce cost.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },

  '/ai-discovery': {
    title: 'AI Discovery | Find the Right AI Opportunities for Your Business',
    description: 'Use Dialgia AI Discovery to identify practical automation opportunities, prioritise business value, and shape a secure enterprise AI roadmap.',
    ogType: 'website',
    schema: { type: 'Service' }
  },

  '/solutions/compare': {
    title: 'Compare Dialgia Solutions | Products, Capabilities & Fit',
    description: 'Compare Dialgia products side by side across capabilities, delivery, integrations, and business outcomes to choose the right enterprise solution.',
    ogType: 'website',
    schema: { type: 'CollectionPage' }
  },

  '/email-banners': {
    title: 'Email Banner Gallery | Dialgia Campaign Design Examples',
    description: 'Browse Dialgia email banner examples for launches, promotions, partnerships, webinars, and customer campaigns, designed for clear enterprise communication.',
    ogType: 'website',
    schema: { type: 'CollectionPage' }
  },

  '/marketing': {
    title: 'Marketing Solutions | Connected Campaigns, CRM & Analytics',
    description: 'Plan, deliver, and measure connected marketing with campaign automation, CRM data, analytics, and AI-assisted workflows in one enterprise platform.',
    ogType: 'website',
    schema: { type: 'Service' }
  },

  // Portfolio
  '/portfolio': {
    title: 'Dialgia Portfolio | Recent Work & Platforms Delivered',
    description: 'Browse selected Dialgia projects and platforms, including enterprise web apps, portals, and product builds delivered for speed, quality, and scale.',
    ogType: 'website',
    schema: { type: 'CollectionPage' }
  },

  // Pricing
  '/pricing': {
    title: 'Dialgia Pricing | Transparent Packages & Enterprise Options',
    description: 'View Dialgia pricing for websites, web apps, SaaS platforms, and support. Clear packages, enterprise options, and delivery you can rely on.',
    ogType: 'website',
    schema: { type: 'WebPage', additionalTypes: ['OfferCatalog'] }
  },

  // Contact
  '/contact': {
    title: 'Contact Dialgia Group | Let\'s Build Something World Class',
    description: 'Speak with Dialgia about your website, SaaS platform, AI agents, or enterprise delivery needs. We\'ll respond quickly and clearly.',
    ogType: 'website',
    schema: { type: 'ContactPage' }
  },

  // Website Wizard
  '/website-wizard': {
    title: 'Website Wizard | Create a World Class Project Scope',
    description: 'Use the Dialgia Website Wizard to define your project scope, requirements, integrations, and goals, then generate a clear PRD-ready brief.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },

  // Price Beat Guarantee
  '/price-beat-guarantee': {
    title: 'Price Beat Guarantee | Dialgia Group',
    description: 'Dialgia\'s Price Beat Guarantee gives you confidence you\'re getting premium delivery at a fair price, with clear scope and accountable outcomes.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },

  // CheckMySite
  '/checkmysite': {
    title: 'CheckMySite | Instant Website Checks by Dialgia',
    description: 'Run quick checks on performance, UX, and fundamentals, then get practical actions to improve your site with enterprise-grade execution.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },

  // Product Pages
  '/products/centurion-global-protect': {
    title: 'Centurion Global Protect | Security & Protection Platform',
    description: 'Centurion Global Protect strengthens security posture with practical controls, visibility, and governance designed for enterprise delivery.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/marketing-sales-suite': {
    title: 'Marketing & Sales Suite | Dialgia Group',
    description: 'A unified marketing and sales platform to manage pipeline, campaigns, reporting, and customer journeys with enterprise-grade controls.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/facilities-management-suite': {
    title: 'Facilities Management Suite | FloHub by Dialgia',
    description: 'A complete facilities management suite for work orders, assets, technicians, reporting, and AI-enabled workflows built to scale.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/digital-newsroom-platform': {
    title: 'Digital Newsroom Platform | CMS & Publishing by Dialgia',
    description: 'A modern digital newsroom platform with CMS, workflows, publishing, and performance tools designed for fast editorial teams and premium UX.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/quality-assurance-system': {
    title: 'Quality Assurance System | AI-Enabled QA by Dialgia',
    description: 'A QA platform to manage evaluations, calibrations, coaching insights, and reporting with enterprise workflows and optional AI scoring.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/call-recording-system': {
    title: 'Call Recording System | Secure Recording & Playback',
    description: 'Secure call recording with controlled access, retention options, search, and compliance-ready handling designed for enterprise teams.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/workforce-management-system': {
    title: 'Workforce Management System | Forecasting & Scheduling',
    description: 'WFM built for accuracy and scale, covering forecasting, scheduling, adherence, shrinkage, and performance insights for operations leaders.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/it-service-desk-platform': {
    title: 'IT Service Desk Platform | Tickets, SLAs & Self Help',
    description: 'A service desk platform with ticketing, SLAs, approvals, and self-help workflows designed for fast resolution and enterprise governance.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/coworking-management-suite': {
    title: 'Coworking Management Suite | Spaces, Members & Billing',
    description: 'Manage coworking spaces with memberships, bookings, payments, access workflows, reporting, and configurable admin controls.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/complete-payroll-system': {
    title: 'Complete Payroll System | Global Payroll by Dialgia',
    description: 'A payroll platform built for scale, with secure workflows, approvals, reporting, and enterprise-ready controls for multi-location operations.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/global-employer-of-record': {
    title: 'Global Employer of Record | Compliance-First EOR',
    description: 'EOR capability designed for compliant hiring, onboarding, and workforce management across regions, with clear workflows and governance.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/complete-hr-management-platform': {
    title: 'Complete HR Management Platform | HRIS by Dialgia',
    description: 'An HR management platform covering people data, lifecycle workflows, policies, reporting, and enterprise-ready access controls.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/passenger-transportation-management': {
    title: 'Passenger Transportation Management | Operations by Dialgia',
    description: 'Coordinate passenger bookings, journeys, drivers, vehicles, communication, and reporting through one secure transportation management platform.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  '/products/time-attendance-system': {
    title: 'Time & Attendance System | Workforce Accuracy by Dialgia',
    description: 'Manage time capture, shifts, approvals, exceptions, and payroll-ready attendance records with secure enterprise workflows and reporting.',
    ogType: 'product',
    schema: { type: 'Product', additionalTypes: ['SoftwareApplication'] }
  },

  // Legal & utility routes (unique metadata per page)
  '/privacy': {
    title: 'Privacy Policy | Dialgia Group',
    description: 'Read the Dialgia Group privacy policy: what personal data we collect, how we use it, your rights, and how to contact our data protection team.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/legal': {
    title: 'Legal & Terms of Service | Dialgia Group',
    description: 'Review Dialgia Group\'s legal terms, service conditions, acceptable use, liability, and contractual details governing use of our platforms.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/cookie-policy': {
    title: 'Cookie Policy | How Dialgia Group Uses Cookies',
    description: 'Understand which cookies Dialgia Group uses, why we use them, how long they last, and how to manage your preferences at any time.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/gdpr-compliance': {
    title: 'GDPR Compliance | Data Protection at Dialgia Group',
    description: 'How Dialgia Group meets GDPR obligations: lawful bases, data subject rights, security controls, DPAs, sub-processors, and breach response.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/sitemap': {
    title: 'Sitemap | Browse Every Dialgia Group Page',
    description: 'Navigate every public Dialgia Group page: products, industries, pricing, portfolio, resources, and support in one structured index.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/blog': {
    title: 'Dialgia Blog | Enterprise SaaS, AI & Delivery Insights',
    description: 'Insights on enterprise SaaS, AI agents, delivery discipline, and modern platform engineering from the Dialgia Group team.',
    ogType: 'website',
    schema: { type: 'CollectionPage' }
  },
  '/careers': {
    title: 'Careers at Dialgia Group | Build World-Class Products',
    description: 'Join Dialgia Group and help build enterprise websites, SaaS platforms, and AI agents with a team obsessed with quality and delivery.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  },
  '/connect': {
    title: 'Connect with Dialgia Group | Partnerships & Integrations',
    description: 'Explore partnership, integration, and collaboration opportunities with Dialgia Group across our enterprise product portfolio.',
    ogType: 'website',
    schema: { type: 'WebPage' }
  }
};

// Default SEO values for routes not in config
export const DEFAULT_SEO: RouteSEOConfig = {
  title: 'Dialgia Group | Enterprise Websites, SaaS Platforms & AI Agents',
  description: 'Dialgia Group builds enterprise websites, SaaS platforms, and AI agents with secure delivery, premium UX, and scalable architecture for modern businesses.',
  ogType: 'website',
  schema: { type: 'WebPage' }
};

const getManifestSEOConfig = (path: string): RouteSEOConfig | null => {
  const entry = getRouteManifestEntry(path);
  if (!entry) return null;

  if (!entry.indexable) {
    return { ...DEFAULT_SEO, noIndex: true };
  }

  if (!entry.sitemap) return null;

  const isProduct = entry.sitemap.category === 'products';
  const isCollection = entry.sitemap.category === 'content';
  const isDemo = entry.sitemap.category === 'demos';

  return {
    title: formatTitle(entry.sitemap.title),
    description: entry.sitemap.description,
    ogType: isProduct ? 'product' : 'website',
    schema: {
      type: isProduct
        ? 'Product'
        : isCollection
          ? 'CollectionPage'
          : isDemo
            ? 'SoftwareApplication'
            : 'WebPage',
      additionalTypes: isProduct ? ['SoftwareApplication'] : undefined,
    },
  };
};

const normalizeSEOPath = (path: string) => {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  return pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
};

// Get SEO config for a path
export const getSEOConfig = (path: string): RouteSEOConfig => {
  const normalizedPath = normalizeSEOPath(path);
  return SEO_CONFIG[normalizedPath] || getManifestSEOConfig(normalizedPath) || DEFAULT_SEO;
};

export const isSelfManagedSEOPath = (path: string) => {
  const routePattern = getRouteManifestEntry(normalizeSEOPath(path))?.path;
  return [
    '/products/category/:categorySlug',
    '/products/:productId',
    '/blog/:slug',
  ].includes(routePattern || '');
};

// Export constants
export { BASE_URL, DEFAULT_OG_IMAGE };
